/* ============================================================
   AEPERION — Shell behaviour
   Theme toggle · ticker cross-fade · year stamp · cart-count stub.

   Vanilla ES module, no framework. Drops into a Shopify theme's assets/
   unchanged; every selector here matches partials/shell.html verbatim.

   ------------------------------------------------------------
   ⚠ REQUIRED IN <head>, BEFORE THE STYLESHEETS — copy this verbatim
   ------------------------------------------------------------

     <script>
       try {
         var t = localStorage.getItem('aep-theme');
         if (t === 'light' || t === 'dark') document.documentElement.dataset.theme = t;
       } catch (e) {}
     </script>

   That is the whole thing: ~150 bytes, synchronous, no dependencies. It
   writes one attribute on <html> and returns. If localStorage is empty
   it writes NOTHING and the prefers-color-scheme block in tokens.css
   takes over — which also means the site keeps following the OS theme
   live for anyone who has never touched the toggle.

   Why it matters. The live aeperion.com ships the opposite of this: the
   NightTheme app injects

     html[data-theme="dark"] body { opacity: 0; transition: opacity .3s; }
     html[data-theme="dark"] body.loaded { opacity: 1; }

   and `.loaded` is added only by a 182 KB deferred third-party script.
   The store defaults to dark, so most visitors get a blank page until
   that script downloads, parses and runs — and a permanently blank page
   if it is blocked. First paint is gated on JS.

   Rules we hold ourselves to, in order:
     1. Nothing in this file, and nothing in any stylesheet, may hide
        <body> or <main> pending JS. No opacity gate, no visibility gate,
        no .loaded class.
     2. The page must render correctly with this module never executing.
        Wrong theme for one paint is a blemish; a blank page is an outage.
     3. Anything a stylesheet can decide, a stylesheet decides. JS only
        persists an explicit user choice.
   ------------------------------------------------------------
   ============================================================ */

import { reduced, initHeader, initReveals } from '@aep/motion';
import { initDither } from '@aep/dither';
import { initImageSettle } from '@aep/img-fade';
import { initCart } from '@aep/cart';

const THEME_KEY = 'aep-theme';

/** Dwell time per ticker phrase. The cross-fade itself is CSS (--d-base). */
const TICKER_DWELL = 5200;

let booted = false;

/* ============================================================
   Theme
   ============================================================ */

function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;                          // private mode, blocked storage
  }
}

const systemDark = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches;

/** What the user is actually looking at right now. */
function activeTheme() {
  return document.documentElement.dataset.theme || readStoredTheme() || (systemDark() ? 'dark' : 'light');
}

/**
 * Apply a theme. `persist` false is used on boot so that a visitor who
 * has never chosen keeps following their OS, rather than being frozen
 * into whatever it happened to be the first time they landed.
 */
export function setTheme(theme, persist = true) {
  const next = theme === 'dark' ? 'dark' : 'light';
  document.documentElement.dataset.theme = next;
  if (persist) {
    try { localStorage.setItem(THEME_KEY, next); } catch { /* non-fatal */ }
  }
  syncToggleLabels();
  return next;
}

function syncToggleLabels() {
  const next = activeTheme() === 'dark' ? 'light' : 'dark';
  document.querySelectorAll('[data-theme-toggle]').forEach((btn) => {
    btn.setAttribute('aria-label', `Switch to ${next} theme`);
  });
}

function initTheme(scope) {
  // Defensive: if the <head> snippet above is missing, apply the stored
  // choice now. Late is worse than never being blank.
  const stored = readStoredTheme();
  if (stored && !document.documentElement.dataset.theme) {
    document.documentElement.dataset.theme = stored;
  }

  syncToggleLabels();

  scope.querySelectorAll('[data-theme-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      setTheme(activeTheme() === 'dark' ? 'light' : 'dark');
    });
  });

  // Still following the OS? Then track it, and keep the button's label honest.
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      if (!document.documentElement.dataset.theme) syncToggleLabels();
    });
}

/* ============================================================
   Ticker
   Three phrases, one visible, cross-faded in place on a timer.
   Explicitly not a marquee: no infinite scroll, no duplicated track,
   nothing moving while you read it.

   CSS owns the fade; this owns only which phrase is current. Reduced
   motion holds phrase one and never starts the timer, which is also
   exactly what the no-JS stylesheet does.
   ============================================================ */

function initTicker(scope) {
  const ticker = scope.querySelector('.ticker');
  if (!ticker) return;

  const phrases = Array.from(ticker.querySelectorAll('.ticker__inner > .label'));
  if (!phrases.length) return;

  let index = 0;
  let timer = 0;

  const show = (n) => phrases.forEach((el, k) => el.classList.toggle('is-current', k === n));

  const stop = () => { clearTimeout(timer); timer = 0; };

  const advance = () => {
    index = (index + 1) % phrases.length;
    show(index);
    timer = setTimeout(advance, TICKER_DWELL);
  };

  const start = () => {
    if (timer || phrases.length < 2 || reduced() || document.hidden) return;
    timer = setTimeout(advance, TICKER_DWELL);
  };

  show(0);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop(); else start();
  });

  // Reduced motion can be turned on mid-session; honour it immediately.
  window
    .matchMedia('(prefers-reduced-motion: reduce)')
    .addEventListener('change', () => {
      stop();
      if (reduced()) { index = 0; show(0); } else { start(); }
    });

  start();
}

/* ============================================================
   Year stamp
   ============================================================ */

function initYear(scope) {
  const year = String(new Date().getFullYear());
  scope.querySelectorAll('[data-year]').forEach((el) => { el.textContent = year; });
}

/* ============================================================
   Which nav item is the page you are on

   shell.css has always styled `.nav a[aria-current='page']` — permanent
   rule, --fg colour, "the line is the whole signal, which is also why
   aria-current gets it permanently" — and nothing has ever written the
   attribute. The state was designed, styled and never reachable: no
   wayfinding in the nav, and nothing announced to a screen reader.

   Done here rather than stamped into each page's markup because the
   shell is copied verbatim into nine files and a hand-maintained
   attribute in nine copies is a thing that goes stale. One comparison
   against location.pathname cannot.
   ============================================================ */

function initNavCurrent(scope) {
  const here = window.location.pathname.replace(/\/index\.html$/, '/');

  scope.querySelectorAll('.nav a[href]').forEach((a) => {
    const target = new URL(a.href, window.location.href).pathname
      .replace(/\/index\.html$/, '/');
    if (target === here) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  });
}

/* ============================================================
   Cart count

   Driven by the `cart:updated` event that src/js/cart.js dispatches on
   document after every add, quantity change, removal and cross-tab sync.
   That indirection is the port seam: swapping the local store in cart.js
   for Shopify's /cart.js endpoints changes where the number comes from
   and nothing at all in this file.
   ============================================================ */

/**
 * Write the cart item count into the header.
 * @param {number|string} n item count
 * @param {boolean} [tick] play the roll — true for a real change, false
 *   when merely re-stating a count on boot, which must be silent.
 */
export function updateCartCount(n, tick = false) {
  const count = Math.max(0, Math.floor(Number(n) || 0));

  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    const changed = el.textContent.trim() !== String(count);
    el.textContent = String(count);
    el.classList.toggle('is-filled', count > 0);

    if (!tick || !changed || reduced()) return;
    // Restart the animation even if the class is still on from the last
    // one: remove, force a style resolution, re-add.
    el.classList.remove('is-ticking');
    void el.offsetWidth;
    el.classList.add('is-ticking');
    el.addEventListener('animationend', () => el.classList.remove('is-ticking'), { once: true });
  });

  // The link ships aria-label="Cart, 0 items"; a stale count there is a
  // lie to screen-reader users, so it moves with the number.
  document.querySelectorAll('.header__cart').forEach((el) => {
    el.setAttribute('aria-label', `Cart, ${count} item${count === 1 ? '' : 's'}`);
  });

  return count;
}

/* ============================================================
   Boot
   ============================================================ */

/**
 * Wire up the shared chrome.
 *
 * @param {object}      [opts]
 * @param {Document|Element} [opts.scope=document]  where to look for the chrome
 * @param {boolean}     [opts.header=true]  bind the scroll show/hide from
 *   motion.js. Pass `false` if the page also calls `initMotion()` — that
 *   binds the header too, and two ScrollTriggers toggling the same two
 *   classes is harmless but pointless.
 */
export function initShell({ scope = document, header = true } = {}) {
  if (booted) return;
  booted = true;

  initTheme(scope);
  initTicker(scope);
  initYear(scope);
  initNavCurrent(scope);

  // Before the shell flag below, deliberately: the observer seeds whatever
  // is already in frame, and doing that while the page is still showing
  // its hidden state is what keeps above-the-fold content from playing an
  // entrance it never earned.
  initReveals(scope === document ? document : scope);

  // The header ships `0`. Anything already in the cart arrives through
  // this listener, which initCart()'s boot publish fires synchronously
  // below — so the number is correct before the first paint after JS,
  // and it does not tick on arrival, only on change.
  document.addEventListener('cart:updated', (e) => {
    updateCartCount(e.detail.item_count, e.detail.reason !== 'boot');
  });
  initCart();

  if (header) {
    const el = scope.querySelector('[data-header]');
    if (el && !el.dataset.headerBound) {
      el.dataset.headerBound = '1';
      initHeader(el);
    }
  }

  // Last, and only after everything above succeeded: tells shell.css the
  // JS-driven states (ticker cross-fade, theme toggle) are safe to show.
  // Nothing that renders content is gated on it.
  document.documentElement.dataset.shell = 'ready';
  initDither();
  initImageSettle();
}

/**
 * Re-scan a subtree that arrived after boot. Shopify replaces section DOM
 * wholesale — the theme editor on every merchant edit, Horizon's section
 * renderer on facet, pagination and variant re-renders — and a replaced node
 * lands outside the reveal observer with its hidden state baked in, stuck at
 * opacity 0 forever. Every scan here is safely re-runnable per element.
 */
export function rescan(root = document) {
  initReveals(root === document ? document : root);
  initImageSettle(root === document ? document : root);
  const el = root === document ? document.querySelector('[data-header]') : root.querySelector('[data-header]');
  if (el && !el.dataset.headerBound) {
    el.dataset.headerBound = '1';
    initHeader(el);
  }
}

/* Armed only under the Shopify runtime — the static site never re-renders a
   subtree, so it should not pay for an always-on observer. shopify:section:load
   covers the theme editor; the MutationObserver covers Horizon's morph-based
   section renderer, which mutates the DOM in place and fires no document-level
   event we can subscribe to. rAF-debounced, and only when an added node brings
   motion markup with it. */
if (typeof window !== 'undefined' && window.Shopify) {
  document.addEventListener('shopify:section:load', (e) => rescan(e.target));

  const MOTION = '[data-reveal], [data-reveal-group], [data-split], [data-header], img[loading="lazy"]';
  let queued = false;
  const mo = new MutationObserver((records) => {
    if (queued) return;
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.matches(MOTION) || n.querySelector(MOTION)) {
          queued = true;
          requestAnimationFrame(() => { queued = false; rescan(document); });
          return;
        }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
}

export default initShell;
